import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FileGroupView, type FileGroup } from "./components/FileGroupView";
import { FileErrorsView } from "./components/FileErrorsView";
import type { ESLintError } from "./LintTriage";

export const TestFailures: React.FC = () => {
  const [errors, setErrors] = useState<ESLintError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const loadErrors = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("http://localhost:9999/api/test-errors");
      if (!res.ok) {
        throw new Error(`Failed to load test errors: ${res.statusText}`);
      }
      const data = await res.json();
      setErrors(data.errors);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load errors on mount
  useEffect(() => {
    loadErrors();
  }, []);

  // Group errors by file
  const errorsByFile: FileGroup[] = React.useMemo(() => {
    const grouped = new Map<string, ESLintError[]>();

    errors.forEach((testError) => {
      const existing = grouped.get(testError.relativeFilePath) || [];
      grouped.set(testError.relativeFilePath, [...existing, testError]);
    });

    // Convert to array and sort by error count (descending)
    return Array.from(grouped.entries())
      .map(([filePath, fileErrors]): FileGroup => ({
        filePath,
        errors: fileErrors,
        eslintCount: 0,
        typescriptCount: 0,
        totalCount: fileErrors.length,
      }))
      .sort((a, b) => b.totalCount - a.totalCount);
  }, [errors]);

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Running tests and collecting failures...
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          This may take 30-60 seconds depending on your test suite size
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 3 }}
        action={
          <Button color="inherit" size="small" onClick={loadErrors}>
            Retry
          </Button>
        }
      >
        {error}
        <Box mt={2}>
          <Typography variant="caption">
            Make sure the devtools server is running: cd devtools && npm run dev
          </Typography>
        </Box>
      </Alert>
    );
  }

  if (errors.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h5" gutterBottom>
          All Tests Passing! 🎉
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          No test failures found. Great job!
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={loadErrors}>
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Test Failures
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {errors.length} failing test{errors.length !== 1 ? "s" : ""} across{" "}
        {errorsByFile.length} file{errorsByFile.length !== 1 ? "s" : ""}
      </Typography>

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Workflow:
        </Typography>
        <Typography variant="body2" component="div">
          1. Click on a file to view all test failures
          <br />
          2. Review the test name, error message, and stack trace
          <br />
          3. Click <strong>Copy Claude Prompt</strong> to get a fix request
          <br />
          4. Paste in terminal:{" "}
          <code
            style={{
              background: "#f5f5f5",
              padding: "2px 4px",
              borderRadius: "3px",
            }}
          >
            claude "Fix this test..."
          </code>
          <br />
          5. After fixing, <strong>refresh this page</strong> to re-run tests
        </Typography>
      </Alert>

      {/* File list or file details */}
      {selectedFile ? (
        <FileErrorsView
          filePath={selectedFile}
          errors={errors.filter((e) => e.relativeFilePath === selectedFile)}
          onBack={() => setSelectedFile(null)}
          onErrorsRefresh={() => {
            setTimeout(() => loadErrors(), 1000);
          }}
        />
      ) : (
        <FileGroupView
          files={errorsByFile}
          onViewFile={setSelectedFile}
          onErrorsRefresh={() => {
            setTimeout(() => loadErrors(), 1000);
          }}
        />
      )}
    </Box>
  );
};
